import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const CHECK_INTERVAL_MS = 60 * 1000; // re-check staleness every minute

/**
 * Forces a full page reload once the app has been open longer than `maxAgeMs`,
 * so long-lived tabs don't keep running a stale bundle. The reload only happens
 * at non-disruptive moments: when the tab is backgrounded or when the user
 * navigates to a new route (both already discard in-page state), never in the
 * middle of an active session on the same view.
 */
export default function useAutoRefresh(maxAgeMs = DEFAULT_MAX_AGE_MS) {
  const loadTimeRef = useRef(Date.now());
  const location = useLocation();

  useEffect(() => {
    const isStale = () => Date.now() - loadTimeRef.current >= maxAgeMs;

    const reloadIfHiddenAndStale = () => {
      if (document.visibilityState === "hidden" && isStale()) {
        window.location.reload();
      }
    };

    const intervalId = setInterval(reloadIfHiddenAndStale, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", reloadIfHiddenAndStale);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", reloadIfHiddenAndStale);
    };
  }, [maxAgeMs]);

  useEffect(() => {
    if (Date.now() - loadTimeRef.current >= maxAgeMs) {
      window.location.reload();
    }
  }, [location.pathname, maxAgeMs]);
}
